import { ErrorMessage } from '@hookform/error-message';
import React, {
    forwardRef,
    Ref,
    useState,
} from 'react';

import { HTMLAttributes } from 'react';

import {
    UseControllerProps,
    useController,
    useFormContext,
} from 'react-hook-form';

import { InputType } from '../../types';

export interface InputFieldV2Props
    extends UseControllerProps {
    inputProps?: HTMLAttributes<HTMLInputElement>;

    label?: string;

    placeholder?: string;

    rightIcon?: any;

    rightIconPress?: () => void;

    containerStyle?: React.CSSProperties;

    textAreaStyle?: React.CSSProperties;

    type?: InputType;

    className?: string;

    isTextArea?: boolean;

    hideErrorColor?: boolean;

    maxLength?: number;

    isDisabled?: boolean;

    rows?: number;

    infoIcon?: string;
    leftIcon?: string;
    id?: string;

}

const InputFieldV2 = forwardRef(
    (
        props: InputFieldV2Props,
        ref: Ref<any>
    ) => {
        const {
            name,
            label,
            rules,
            defaultValue,
            rightIcon,
            rightIconPress,
            className,
            isTextArea,
            maxLength,
            rows = 4,
            infoIcon,
            leftIcon,
            id
        } = props;

        const formContext = useFormContext();

        const { formState, setValue } = formContext;

        const { field } = useController({
            name,
            rules,
            defaultValue,
        });

        const [isFocused, setIsFocused] =
            useState(false);

        const hasError = Boolean(
            formState?.errors[name]
        );

        const getWrapperClass = () => {
            if (hasError) {
                return 'input-field-error';
            }

            if (isFocused) {
                return 'input-field-focused';
            }

            return '';
        };

        return (
            <div
                style={props?.containerStyle}
                className={`input-field-wrapper ${className || ''}`}
            >
                {/* LABEL */}

                {label && (
                    <div className="input-field-label-row">
                        <label
                            htmlFor={name}
                            className="input-field-label"
                        >
                            {label}
                        </label>

                        {infoIcon && (
                            <img
                                src={infoIcon}
                                alt="info"
                                className="input-field-info-icon"
                            />
                        )}
                    </div>
                )}

                {/* INPUT BOX */}

                <div className={`input-field-box ${getWrapperClass()}`}>
                    {leftIcon && (
                        <img
                            src={leftIcon}
                            alt="info"
                            className="input-field-info-icon-money"
                        />
                    )}
                    {isTextArea ? (
                        <textarea
                            ref={ref}
                            id={id || name}
                            rows={rows}
                            placeholder={props?.placeholder}
                            value={field.value || ''}
                            disabled={props?.isDisabled}
                            maxLength={maxLength}
                            style={props?.textAreaStyle}
                            className="input-field-textarea"
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => {
                                setIsFocused(false);
                                field?.onBlur?.();
                            }}
                            onChange={field.onChange}
                        />
                    ) : (
                        <input
                            ref={ref}
                            id={id || name}
                            type={props?.type || 'text'}
                            placeholder={props?.placeholder}
                            value={field.value || ''}
                            disabled={props?.isDisabled}
                            maxLength={maxLength}
                            className="input-field-input"
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => {
                                setIsFocused(false);
                                field?.onBlur?.();
                            }}
                            onChange={field.onChange}
                            {...props?.inputProps}
                        />
                    )}

                    {/* RIGHT ICON */}

                    {rightIcon && (
                        <img
                            src={rightIcon}
                            onClick={rightIconPress}
                            alt="icon"
                            className="input-field-right-icon"
                        />
                    )}

                    {/* ERROR ICON */}

                    {hasError &&
                        !isFocused &&
                        !isTextArea && (
                            <img
                                src="/assets/images/error.svg"
                                alt="error"
                                className="input-field-error-icon"
                            />
                        )}

                    {/* CLEAR ICON */}

                    {!hasError &&
                        !isFocused &&
                        field.value &&
                        !isTextArea && (
                            <img
                                src="/assets/images/cross.svg"
                                alt="clear"
                                className="input-field-clear-icon"
                                onClick={() => {
                                    if (props?.isDisabled)
                                        return;

                                    setValue(name, '');

                                    field.onBlur?.();
                                }}
                            />
                        )}
                </div>

                {/* ERROR MESSAGE */}

                <ErrorMessage
                    errors={formState.errors}
                    name={props?.name}
                    render={({ message }) => (
                        <span className="input-field-error-text">
                            {message}
                        </span>
                    )}
                />
            </div>
        );
    }
);

export default InputFieldV2;